import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { Reader } from '@/api/client';

/**
 * Префикс ключа React Query для кэша списка чтецов в каталоге.
 * Инвалидация по `['readers','table']` затрагивает все варианты, в т.ч. разные строки поиска (`…,'infinite', q`).
 */
export const READERS_TABLE_QUERY_KEY: readonly ['readers', 'table'] = ['readers', 'table'];

export type ReadersCatalogListPage = {
  items: Reader[];
  hasMore: boolean;
  nextOffset: number;
};

/** Размер страницы списка чтецов (согласован с серверным max limit в GET /readers). */
export const READERS_CATALOG_LIST_PAGE_SIZE = 100;

export function readersTableInfiniteQueryKey(
  searchTrimmed: string
): readonly ['readers', 'table', 'infinite', string] {
  return ['readers', 'table', 'infinite', searchTrimmed];
}

export function isReadersTableInfiniteQueryKey(queryKey: unknown): queryKey is readonly [
  'readers',
  'table',
  'infinite',
  string,
] {
  return (
    Array.isArray(queryKey) &&
    queryKey[0] === 'readers' &&
    queryKey[1] === 'table' &&
    queryKey[2] === 'infinite' &&
    typeof queryKey[3] === 'string'
  );
}

export type ReadersTableInfiniteSnapshot = Array<
  [readonly unknown[], InfiniteData<ReadersCatalogListPage> | undefined]
>;

export function snapshotReadersTableInfiniteQueries(queryClient: QueryClient): ReadersTableInfiniteSnapshot {
  return queryClient.getQueriesData<InfiniteData<ReadersCatalogListPage>>({
    predicate: (q) => isReadersTableInfiniteQueryKey(q.queryKey),
  });
}

export function restoreReadersTableInfiniteQueries(
  queryClient: QueryClient,
  snap: ReadersTableInfiniteSnapshot
): void {
  for (const [key, data] of snap) {
    queryClient.setQueryData(key, data);
  }
}

/** Найти чтеца в любом закэшированном списке каталога (для свежих тегов и т.п.). */
export function findReaderInTableCaches(queryClient: QueryClient, readerId: string): Reader | undefined {
  const entries = queryClient.getQueriesData<InfiniteData<ReadersCatalogListPage>>({
    predicate: (q) => isReadersTableInfiniteQueryKey(q.queryKey),
  });
  for (const [, data] of entries) {
    if (!data) continue;
    for (const page of data.pages) {
      const hit = page.items.find((r) => r.id === readerId);
      if (hit) return hit;
    }
  }
  return undefined;
}

export function cancelReadersTableListQueries(queryClient: QueryClient): Promise<void> {
  return queryClient.cancelQueries({
    predicate: (q) => isReadersTableInfiniteQueryKey(q.queryKey),
  });
}

/** Справочник тегов чтецов (GET /reader-tags) — инвалидация при readers:catalogChanged */
export const READER_TAGS_CATALOG_QUERY_KEY: readonly ['readerTags', 'catalog'] = ['readerTags', 'catalog'];

/** Справочник тегов фонотеки (GET /music-tags) */
export const MUSIC_TAGS_CATALOG_QUERY_KEY: readonly ['musicTags', 'catalog'] = ['musicTags', 'catalog'];

function mapReaderInInfinitePages(
  old: InfiniteData<ReadersCatalogListPage> | undefined,
  readerId: string,
  mapRow: (row: Reader) => Reader
): InfiniteData<ReadersCatalogListPage> | undefined {
  if (!old) return old;
  let touched = false;
  const pages = old.pages.map((page) => {
    const idx = page.items.findIndex((r) => r.id === readerId);
    if (idx === -1) return page;
    touched = true;
    const nextItems = [...page.items];
    nextItems[idx] = mapRow(nextItems[idx]!);
    return { ...page, items: nextItems };
  });
  if (!touched) return old;
  return { ...old, pages };
}

/**
 * Оптимистично обновить строку чтеца во всех закэшированных списках каталога (разные строки поиска).
 */
export function optimisticPatchReaderInTableCaches(
  queryClient: QueryClient,
  readerId: string,
  patch: Partial<Reader> | ((prev: Reader) => Reader)
): void {
  queryClient.setQueriesData<InfiniteData<ReadersCatalogListPage>>(
    { predicate: (q) => isReadersTableInfiniteQueryKey(q.queryKey) },
    (old) =>
      mapReaderInInfinitePages(old, readerId, (row) =>
        typeof patch === 'function' ? patch(row) : { ...row, ...patch }
      )
  );
}

/**
 * Слить ответ PATCH чтеца в кэш каталога (глобально staleTime: Infinity — без setQueryData UI не обновляется).
 * Обновляет все варианты списка (`infinite`, разные q), где встречается person_id.
 */
export function patchReaderInTableCache(queryClient: QueryClient, next: Reader): void {
  queryClient.setQueriesData<InfiniteData<ReadersCatalogListPage>>(
    { predicate: (q) => isReadersTableInfiniteQueryKey(q.queryKey) },
    (old) => mapReaderInInfinitePages(old, next.id, (row) => ({ ...row, ...next }))
  );
}

/** Инвалидация поиска/комбо чтецов — без ключа таблицы, уже обновлённой через patch. */
export function invalidateReaderCachesExceptTable(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({
    predicate: (q) =>
      Array.isArray(q.queryKey) && q.queryKey[0] === 'readers' && q.queryKey[1] !== 'table',
  });
}
